/**
 * Action handlers for the Results, Developer and File surfaces. OWNER: workstream F.
 *
 * TODO(workstream C): register these on the action bus once `@/shell/actions`
 * exists — the whole hookup is:
 *
 *   import { registerAction } from "@/shell/actions";
 *   import { WORKSTREAM_F_ACTIONS } from "@/results/actions";
 *   for (const [id, run] of Object.entries(WORKSTREAM_F_ACTIONS)) registerAction(id, run);
 *
 * The tabs call the same functions directly, so the UI works with or without the
 * ribbon.
 */

import { getSystemStatus, openPath, previewCommand, revealPath, saveCase, splitError } from "@/ipc/api";
import { pickAndOpenSubject } from "@/pipeline/actions";
import type { SystemStatus } from "@/ipc/contract";
import { copyText } from "./clipboard";
import { currentJobSpec, shell, useResultsStore, type ExportFormat, type FileSection } from "./state";

const store = () => useResultsStore.getState();

// --------------------------------------------------------------- results ---

/**
 * The report is produced by the pipeline's `report` stage inside the container —
 * there is no separate report generator in the desktop app, so this hands the
 * user back to the Pipeline tab instead of faking a document.
 */
export function generateReport(): void {
  const bridge = shell();
  if (bridge.runStages) {
    bridge.runStages(["report"]);
    return;
  }
  store().setNotice(
    "Reports are written by the pipeline's Reporting stage. Re-run that stage from the Pipeline tab to regenerate them.",
  );
  bridge.navigateTab?.("pipeline");
}

export function refreshResults(): void {
  void store().loadSummary(true);
}

export function reprocess(): void {
  const bridge = shell();
  if (bridge.runStages) {
    bridge.runStages(["inference", "report"]);
    return;
  }
  store().setNotice("Re-processing starts a new job — configure and launch it from the Pipeline tab.");
  bridge.navigateTab?.("pipeline");
}

const exportAction = (format: ExportFormat) => () => void store().runExport([format]);

// ------------------------------------------------------------- developer ---

/** `preview_command` builds the exact argv the runner would exec — it never runs it. */
export async function copyDockerCommand(): Promise<void> {
  const spec = currentJobSpec();
  if (!spec) {
    store().setNotice("Open a case first — the docker command is built from its manifest.");
    return;
  }
  try {
    const preview = await previewCommand(spec);
    const ok = await copyText(preview.display);
    store().setNotice(ok ? "Docker command copied to the clipboard." : "Could not reach the clipboard.");
  } catch (error) {
    store().setNotice(splitError(error).message);
  }
}

/**
 * An interactive shell in the same image. `-it` is correct here (a human is
 * attaching a terminal) and wrong for the pipeline run itself, which is piped.
 */
export function containerShellCommand(image: string | null): string {
  return `docker run --rm -it --entrypoint /bin/bash ${image ?? "openads:gpu"}`;
}

export async function copyShellCommand(image?: string | null): Promise<void> {
  let reference = image ?? null;
  if (!reference) {
    try {
      reference = (await getSystemStatus()).docker.selectedImage;
    } catch {
      reference = null;
    }
  }
  const ok = await copyText(containerShellCommand(reference));
  store().setNotice(ok ? "Container shell command copied." : "Could not reach the clipboard.");
}

export async function openOutputFolder(): Promise<void> {
  const manifest = store().caseManifest;
  if (!manifest) {
    store().setNotice("Open a case first.");
    return;
  }
  try {
    await revealPath(`${manifest.outputRoot}/${manifest.subjectId}`);
  } catch (error) {
    store().setNotice(splitError(error).message);
  }
}

export interface RuntimeInfo {
  appVersion: string;
  platform: string;
  tauriVersion: string | null;
  webview: string;
}

/**
 * Everything here is read from the running process. `getTauriVersion` only
 * exists inside the Tauri webview, so in a browser dev session it stays null
 * rather than being guessed.
 */
export async function runtimeInfo(status: SystemStatus | null): Promise<RuntimeInfo> {
  let tauriVersion: string | null = null;
  try {
    const app = await import("@tauri-apps/api/app");
    tauriVersion = await app.getTauriVersion();
  } catch {
    tauriVersion = null;
  }
  return {
    appVersion: status?.appVersion ?? "unknown",
    platform: status?.platform ?? navigator.platform,
    tauriVersion,
    webview: navigator.userAgent,
  };
}

export function systemInfoText(status: SystemStatus | null, runtime: RuntimeInfo): string {
  const lines = [
    `ADS Desk ${runtime.appVersion}`,
    `Platform:        ${runtime.platform}`,
    `Tauri:           ${runtime.tauriVersion ?? "unavailable (running outside the Tauri webview)"}`,
    `Webview:         ${runtime.webview}`,
  ];
  if (status) {
    lines.push(
      `Docker:          ${status.docker.available ? `${status.docker.version ?? "unknown version"} (daemon ${status.docker.daemonRunning ? "running" : "stopped"})` : "unavailable"}`,
      `nvidia runtime:  ${status.docker.gpuRuntimeAvailable ? "present" : "absent"}`,
      `Selected image:  ${status.docker.selectedImage ?? "none detected"}`,
      `GPU driver:      ${status.gpu.driverVersion ?? "—"} / CUDA ${status.gpu.cudaVersion ?? "—"}`,
    );
    for (const device of status.gpu.devices) {
      lines.push(
        `  GPU ${device.index}:       ${device.name} — ${device.memoryUsedMb} / ${device.memoryTotalMb} MB`,
      );
    }
    for (const disk of status.storage) {
      const freeGb = (disk.freeBytes / 1e9).toFixed(1);
      const totalGb = (disk.totalBytes / 1e9).toFixed(1);
      lines.push(`  Disk ${disk.path}: ${freeGb} GB free of ${totalGb} GB`);
    }
  }
  return lines.join("\n");
}

export async function copySystemInfo(): Promise<void> {
  let status: SystemStatus | null = null;
  try {
    status = await getSystemStatus();
  } catch {
    status = null;
  }
  const ok = await copyText(systemInfoText(status, await runtimeInfo(status)));
  store().setNotice(ok ? "System information copied." : "Could not reach the clipboard.");
}

// ------------------------------------------------------------------ file ---

function openBackstage(section: FileSection): void {
  store().setFileSection(section);
  shell().navigateTab?.("file");
}

export function newCase(): void {
  const bridge = shell();
  if (bridge.newCase) {
    bridge.newCase();
    return;
  }
  openBackstage("new");
}

export async function openCaseFromDisk(): Promise<void> {
  try {
    await pickAndOpenSubject();
  } catch (error) {
    store().setNotice(splitError(error).message);
  }
}

export async function saveActiveCase(): Promise<void> {
  const manifest = store().caseManifest;
  if (!manifest) {
    store().setNotice("No case is open.");
    return;
  }
  try {
    await saveCase(manifest);
    store().setNotice(`Saved ${manifest.subjectId} to ${manifest.outputRoot}/${manifest.subjectId}/case.json`);
  } catch (error) {
    store().setNotice(splitError(error).message);
  }
}

export function openPreferences(): void {
  openBackstage("preferences");
}

/** Closes the window; the Rust side decides whether to prompt for unsaved work. */
export async function exitApp(): Promise<void> {
  try {
    const windowApi = await import("@tauri-apps/api/window");
    await windowApi.getCurrentWindow().close();
  } catch {
    store().setNotice("Exit is only available in the desktop build.");
  }
}

export async function openFilePath(path: string): Promise<void> {
  try {
    await openPath(path);
  } catch (error) {
    store().setNotice(splitError(error).message);
  }
}

// ------------------------------------------------------------- registry ---

function showResults(): void {
  shell().navigateTab?.("results");
}

export const WORKSTREAM_F_ACTIONS: Record<string, () => void> = {
  "results.generateReport": generateReport,
  "results.refresh": refreshResults,
  "results.reprocess": reprocess,
  "results.show.summary": showResults,
  "results.show.report": showResults,
  "results.show.timeSeries": showResults,
  "results.show.regional": showResults,
  "results.show.qc": showResults,
  "results.show.snapshots": showResults,
  "results.show.compare": showResults,
  "report.edit": showResults,
  "report.addNote": showResults,
  "export.pdf": exportAction("pdf"),
  "export.csv": exportAction("csv"),
  "export.nifti": exportAction("nifti"),
  "export.json": exportAction("json"),
  "dev.copyDockerCommand": () => void copyDockerCommand(),
  "dev.copyShellCommand": () => void copyShellCommand(),
  "dev.openOutput": () => void openOutputFolder(),
  "dev.systemInfo": () => void copySystemInfo(),
  "file.newCase": newCase,
  "file.openCase": () => void openCaseFromDisk(),
  "file.saveCase": () => void saveActiveCase(),
  "file.preferences": openPreferences,
  "file.exit": () => void exitApp(),
};
